/**
 * The query that feeds `sourcing.ts`.
 *
 * Kept apart from it on purpose: `sourcing.ts` has no imports so the Worker's
 * test suite can load it directly, and this file exists only to turn
 * `venue_source_recipes`, `recipe_coverage`, `recipes` and `harvest_runs`
 * into the `SourceRecipe[]` it composes a sentence from. Nothing here is
 * cached or stored — called again, it reads whatever is true now.
 */
import { supabase } from './supabase'
import type { RecipeStatus, SourceRecipe } from './sourcing'

/** How many recent runs to look at while finding the latest one per recipe.
 *  A venue is touched by one recipe almost always and three at most, so ten
 *  runs each is generous; bounded rather than unbounded so a recipe that
 *  runs hourly cannot make this one query cross the 1000-row PostgREST cap. */
const RUNS_PER_RECIPE = 10

export async function fetchVenueSourcing(venueId: string): Promise<SourceRecipe[]> {
  const touching = await supabase
    .from('venue_source_recipes')
    .select('source_recipe, rows_here')
    .eq('venue_id', venueId)
  if (touching.error) throw touching.error

  const rows = (touching.data ?? []).filter(
    (r): r is { source_recipe: string; rows_here: number } => !!r.source_recipe,
  )
  if (!rows.length) return []

  const recipeIds = [...new Set(rows.map(r => r.source_recipe))]
  const rowsHereById = new Map(rows.map(r => [r.source_recipe, r.rows_here]))

  const [rc, cov, runs] = await Promise.all([
    supabase.from('recipes')
      .select('id,name,status,last_ok_at,refresh_hours,place_id')
      .in('id', recipeIds),
    supabase.from('recipe_coverage').select('source_recipe,places').in('source_recipe', recipeIds),
    supabase.from('harvest_runs')
      .select('recipe_id,status,rows_valid,started_at')
      .in('recipe_id', recipeIds)
      .order('started_at', { ascending: false })
      .limit(RUNS_PER_RECIPE * recipeIds.length),
  ])
  if (rc.error) throw rc.error
  if (cov.error) throw cov.error
  if (runs.error) throw runs.error

  const recipes = rc.data ?? []

  // `recipes.place_id` can name a hall rather than the building the admin
  // screen edits — the Philharmoniker's own recipes are filed at "Grosser
  // Saal", not at "Philharmonie Berlin". One small lookup resolves each
  // candidate place to its building, mirroring what `venue_source_recipes`
  // already did for the rows themselves.
  const placeIds = [...new Set(recipes.map(r => r.place_id).filter((p): p is string => !!p))]
  const parentOf = new Map<string, string | null>()
  if (placeIds.length) {
    const pv = await supabase.from('venues').select('id,parent_id').in('id', placeIds)
    if (pv.error) throw pv.error
    for (const v of pv.data ?? []) parentOf.set(v.id, v.parent_id)
  }

  const coverageById = new Map((cov.data ?? []).map(c => [c.source_recipe, c.places]))

  // `runs` comes back newest first; the first row seen for a recipe is its
  // latest, and later ones for the same id are skipped — the same pattern
  // Home.tsx already uses for the health strip's `lastRun`.
  const lastRunById = new Map<string, { status: string; rowsValid: number | null; startedAt: string }>()
  for (const r of runs.data ?? []) {
    if (!r.recipe_id || lastRunById.has(r.recipe_id)) continue
    lastRunById.set(r.recipe_id, { status: r.status, rowsValid: r.rows_valid, startedAt: r.started_at })
  }

  return recipes.map(r => ({
    id: r.id,
    name: r.name,
    isOwn: r.place_id === venueId || (!!r.place_id && parentOf.get(r.place_id) === venueId),
    status: r.status as RecipeStatus,
    lastOkAt: r.last_ok_at,
    refreshHours: r.refresh_hours,
    coversPlaces: coverageById.get(r.id) ?? null,
    lastRun: lastRunById.get(r.id) ?? null,
    rowsHere: rowsHereById.get(r.id) ?? 0,
  }))
}
